import { useEffect, useRef } from 'react'
import { Alert, Col, DatePicker, Descriptions, Form, Input, Modal, Row, Select } from 'antd'
import type { Dayjs } from 'dayjs'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { tenantToday } from '@/shared/lib/dayRange'
import { newIdempotencyKey } from '@/shared/lib/idempotency'
import { formatMoneyValue, type Money } from '@/shared/lib/money'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { fetchPaymentAccounts, payDistribution, type Distribution } from './stakeholderApi'

interface DistributionPayModalProps {
  distribution: Distribution | null
  onClose: () => void
}

interface FormValues {
  amount: Money | null
  paidOn: Dayjs
  accountId: string
  reference?: string
  note?: string
}

const text = (value: string | undefined): string | null => (value?.trim() ? value.trim() : null)

export function DistributionPayModal({ distribution, onClose }: DistributionPayModalProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<FormValues>()
  const key = useRef('')

  useEffect(() => {
    if (!distribution) return
    key.current = newIdempotencyKey('stakeholder-payment')
    form.setFieldsValue({ amount: distribution.remaining, paidOn: tenantToday(), reference: '', note: '' })
  }, [distribution, form])

  const accounts = useQuery({
    queryKey: queryKeys.stakeholder.paymentAccounts,
    queryFn: ({ signal }) => fetchPaymentAccounts(signal),
    enabled: distribution !== null,
  })

  const pay = useMutation({
    mutationFn: (values: FormValues) => {
      if (!distribution) throw new Error('distribution')
      return payDistribution(
        distribution.id,
        {
          amountMinor: values.amount?.amount ?? 0,
          paidOn: values.paidOn.format('YYYY-MM-DD'),
          accountId: values.accountId,
          reference: text(values.reference),
          note: text(values.note),
        },
        key.current,
      )
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stakeholder.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      onClose()
    },
  })

  const close = () => {
    pay.reset()
    onClose()
  }

  return (
    <Modal
      open={distribution !== null}
      title={t('stakeholder.pay.title')}
      okText={t('stakeholder.pay.submit')}
      cancelText={t('common.cancel')}
      confirmLoading={pay.isPending}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => pay.mutate(values))}
      destroyOnHidden
      width={640}
    >
      {pay.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(pay.error, t)} />
      ) : null}
      {distribution ? (
        <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
          <Descriptions.Item label={t('stakeholder.distribution.stakeholder')}>
            {distribution.stakeholder.code} · {distribution.stakeholder.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('stakeholder.distribution.period')}>{distribution.periodCode}</Descriptions.Item>
          <Descriptions.Item label={t('stakeholder.distribution.remaining')}>
            {formatMoneyValue(distribution.remaining)}
          </Descriptions.Item>
        </Descriptions>
      ) : null}
      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={pay.isPending}>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="amount"
              label={t('stakeholder.pay.amount')}
              rules={[
                {
                  validator: (_, value: Money | null) =>
                    value && value.amount > 0 && distribution && value.amount <= distribution.remaining.amount
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('stakeholder.validation.paymentAmount'))),
                },
              ]}
            >
              <MoneyInput minMinor={1} maxMinor={distribution?.remaining.amount} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="paidOn" label={t('stakeholder.pay.paidOn')} rules={[{ required: true, message: t('stakeholder.validation.date') }]}>
              <DatePicker style={{ width: '100%' }} disabledDate={(day) => day.isAfter(tenantToday(), 'day')} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              name="accountId"
              label={t('stakeholder.capital.moneyAccount')}
              extra={t('stakeholder.capital.moneyAccountHint')}
              rules={[{ required: true, message: t('stakeholder.validation.account') }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                loading={accounts.isPending}
                options={(accounts.data ?? []).map((a) => ({ value: a.id, label: `${a.code} · ${seededName('accounts', a.code, a.name, t)}` }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="reference" label={t('stakeholder.capital.reference')}>
              <Input maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="note" label={t('stakeholder.capital.note')}>
              <Input maxLength={500} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
